import {SaveAsSharp} from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {useEffect, useState} from "react";

import PageTitle from "../components/PageTitle";
import {GeneralLayout} from "../layouts/GeneralLayout";
import {
    useGetReadOnlyModeQuery,
    useGetSystemParametersQuery,
    useSetReadOnlyModeMutation,
    useSetSystemParameterMutation
} from "../store/system/systemApi";
import copyObject from "../utils/copyObject";
import {
    formatReadOnlyModeStartsAt,
    fromMoscowDateTimeLocal,
    toMoscowDateTimeLocal
} from "../utils/readOnlyModeDateTime";

export default function SystemParametersPage() {
    const [isSaving, setIsSaving] = useState(false);
    const {data} = useGetSystemParametersQuery();
    const [copyOfData, setCopyOfData] = useState(null);
    const [setSystemParameterRequest] = useSetSystemParameterMutation();
    const {data: readOnlyMode, isFetching: isReadOnlyModeLoading} = useGetReadOnlyModeQuery();
    const [setReadOnlyMode, {isLoading: isReadOnlyModeSaving}] = useSetReadOnlyModeMutation();
    const [readOnlyModeStartsAt, setReadOnlyModeStartsAt] = useState('');
    
    useEffect(() => {
        setCopyOfData(copyObject(data));
    }, [data]);

    useEffect(() => {
        setReadOnlyModeStartsAt(toMoscowDateTimeLocal(readOnlyMode?.startsAt));
    }, [readOnlyMode?.startsAt]);

    const handleSave = async (parameter) => {
        setIsSaving(true);

        await setSystemParameterRequest({data: {key: parameter.key, value: parameter.value, description: parameter.description}}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                setIsSaving(false);
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });

                setIsSaving(false);
            }
        }).catch(() => {
            alert('Ошибка');
            setIsSaving(false);
        });
    };

    const handleReadOnlyModeChange = async () => {
        if (!readOnlyModeStartsAt) {
            alert('Укажите дату и время перехода в режим «Только чтение»');
            return;
        }

        const formattedStartsAt = formatReadOnlyModeStartsAt(
            fromMoscowDateTimeLocal(readOnlyModeStartsAt));
        if (!window.confirm(
            `Установить переход системы в режим «Только чтение» на ${formattedStartsAt}?`))
            return;

        try {
            await setReadOnlyMode({
                startsAt: fromMoscowDateTimeLocal(readOnlyModeStartsAt)
            }).unwrap();
        } catch (error) {
            alert(error?.data?.errors?.[0] || 'Не удалось установить дату перехода');
        }
    };

    return (
        <GeneralLayout>
            <PageTitle title={`Системные параметры`} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {data === null || data === undefined ? <CircularProgress /> : <></>}
                            Системные параметры
                        </Typography>
                    </Stack>

                    <Card>
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="h6">Режим «Только чтение»</Typography>
                                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{sm: 'center'}}>
                                    <TextField
                                        label="Дата и время перехода (МСК)"
                                        type="datetime-local"
                                        value={readOnlyModeStartsAt}
                                        disabled={isReadOnlyModeLoading || isReadOnlyModeSaving}
                                        onChange={(event) => setReadOnlyModeStartsAt(event.target.value)}
                                        inputProps={{step: 1}}
                                        InputLabelProps={{shrink: true}}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveAsSharp/>}
                                        disabled={isReadOnlyModeLoading || isReadOnlyModeSaving || !readOnlyModeStartsAt}
                                        onClick={handleReadOnlyModeChange}
                                    >
                                        Сохранить
                                    </Button>
                                </Stack>
                                <Alert severity={readOnlyMode?.enabled ? 'warning' : 'info'}>
                                    {readOnlyMode?.enabled
                                        ? 'Новые операции пользователей по созданию, изменению и удалению данных блокируются. Уже выполняющиеся операции будут завершены. Для администраторов ограничение не действует.'
                                        : readOnlyMode?.startsAt
                                            ? `Система будет временно переведена в режим «Только чтение» ${formatReadOnlyModeStartsAt(readOnlyMode.startsAt)}.`
                                            : 'Загрузка даты перехода...'}
                                </Alert>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <Table size={'small'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Ключ</TableCell>
                                    <TableCell>Значение</TableCell>
                                    <TableCell>Описание</TableCell>
                                    <TableCell><></></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {copyOfData?.data?.filter((item) => item.key !== 3).map((item) => (
                                    <TableRow key={item.systemParameterId}>
                                        <TableCell>{item.systemParameterId}</TableCell>
                                        <TableCell>{item.key}</TableCell>
                                        <TableCell>
                                            <TextField size={'small'}
                                                        disabled={isSaving}
                                                        defaultValue={item.value}
                                                        onChange={(e) => {item.value = e.target.value}}/>
                                        </TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>
                                            <IconButton 
                                                disabled={isSaving}
                                                onClick={() => handleSave(item)}
                                                size={'small'}><SaveAsSharp /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>

                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
